import createDocumentData from "../../utils/createDocumentData";
import { fromDotObject } from "../../utils/dotObject";
import mergeDocumentData from "../../utils/mergeDocumentData";
import runTransaction from "../../utils/runTransaction";
import updateDocumentData from "../../utils/updateDocumentData";

import getPublisherDistributionPresets from "../distribution/utils/getPublisherDistributionPresets";
import createImprintData from "../imprints/createImprintData";
import createSerieData from "../series/createSerieData";
import createWorkData from "../works/createWorkData";

import articleStatuses from "./constants/articleStatuses";
import dedupeContributors from "./data/dedupeContributors";
import getArticleVersionData from "./data/getArticleVersionData";
import updateArticleChannelDeliveryNeeded from "./data/updateArticleChannelDeliveryNeeded";
import updateArticleChannelIds from "./data/updateArticleChannelIds";
import { updateArticleDeliveryStatus } from "./data/updateArticleDeliveryStatus";
import updateArticlePublicationStatus from "./data/updateArticlePublicationStatus";
import { Article } from "./types/Article";
import { ArticleVersion } from "./types/ArticleVersion";
import validateArticleISBN from "./validateArticleISBN";

export default async function updateArticle(
	firebase,
	data,
	{ user, transaction, dry = false, disableTakedowns = false, disableDeliveryNeeded = false, debug } = {},
) {
	data = fromDotObject(data);

	if (data.isbn) {
		data.isbn = data.isbn.replaceAll("-", "").trim();
		await validateArticleISBN(firebase.firestore(), data);
	}

	if (data.contributors) {
		data.contributors = dedupeContributors(data.contributors);
	}

	if (dry) {
		return data;
	}

	if (user) {
		data.updatedBy = user;
	}

	return runTransaction(
		firebase,
		[data.ref, data.publisher?.ref, data.work?.ref, data.imprint?.ref, data.serie?.ref, data.production?.ref],
		async (transaction, [article, publisher, work, imprint, serie, production]) => {
			if (article?.exists && article.status !== articleStatuses.DRAFT && data.status === articleStatuses.DRAFT) {
				throw new Error("Cannot set article to draft");
			}

			const status = data.status || article.status;

			if (publisher) {
				data.publisher = publisher;
			}

			publisher = await transaction.getDocument(data.publisher?.ref || article.publisher.ref);

			// Update the duration for default artifact
			if (data.artifact?.duration !== undefined && data.duration === undefined) {
				data.duration = data.artifact.duration;
			}

			// If the article is ready and has no channels, apply the publisher presets
			if (data.status === articleStatuses.READY && (!data.channelIds || data.channelIds?.length === 0)) {
				const presets = getPublisherDistributionPresets({
					article: mergeDocumentData(article, data),
					publisher,
				});

				data.channelIds = [...new Set(presets.map(({ channel }) => channel.id))];
			}

			if (status === articleStatuses.READY) {
				// Update article channels data when channelIds are updated
				if (data.channelIds) {
					data = await updateArticleChannelIds(firebase, {
						publisher,
						article,
						data,
						transaction,
						disableTakedowns,
						disableDeliveryNeeded,
					});
				}

				// Updated delivery needed status if metadata has changed (per article channel)
				if (!disableDeliveryNeeded) {
					data = await updateArticleChannelDeliveryNeeded(firebase, { article, data, transaction });
				}

				// Update global delivery status for article (based on channel delivery status)
				if (data.channels || data.channelIds) {
					data = updateArticleDeliveryStatus(article, data);
					data = updateArticlePublicationStatus(article, data, { publisher });
				}
			}

			// If the work does not exist, create it
			if (work && !work?.exists) {
				transaction.set(work.ref, createWorkData(firebase, { ...data, ...data.work, publisher }));
			}

			// If the imprint does not exist, create it
			if (imprint && !imprint?.exists) {
				transaction.set(imprint.ref, createImprintData(firebase, { ...data.imprint, publisher }));
			}

			// If the serie does not exist, create it
			if (serie && !serie?.exists) {
				transaction.set(serie.ref, createSerieData(firebase, { ...data.serie, publisher }));
			}

			// Add the article to a production
			if (production) {
				transaction.update(production.ref, {
					articleIds: firebase.firestore.FieldValue.arrayUnion(article.id),
					[`articles.${article.id}.type`]: data.type || article.type,
				});
			}

			const parsedData = Article.partial().parse(updateDocumentData(firebase, data));

			const mergedArticle = mergeDocumentData(article, { ...parsedData, exists: true });

			transaction.set(article.ref, parsedData, { merge: true });

			// Save a version when a versioned field has changed
			const versionData = article.exists && getArticleVersionData(article, mergedArticle);

			if (versionData) {
				const version = ArticleVersion.parse(
					createDocumentData(firebase, `articles/${article.id}/versions`, {
						...versionData,
						createdBy: user,
					}),
				);

				transaction.set(version.ref, version);
			}

			return mergedArticle;
		},
		transaction,
		debug,
	);
}
